import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Clock3, Package, ShoppingBag } from 'lucide-react'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { toast } from 'sonner'

import { trackEvent } from '../lib/analyticsEvents'

export default function OrderDetailsPage() {
  const STATUS_FLOW = ['confirmed', 'preparing', 'completed']

  const STATUS_LABELS = {
    confirmed: 'Commande confirmée',
    preparing: 'Préparation de la commande',
    completed: 'Commande terminée',
    cancelled: 'Commande annulée',
  }

  const STATUS_COLORS = {
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  }

  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isAdmin, isAuthenticated, loading: authLoading } = useAuth()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  const getNextStatus = (status) => {
    if (status === 'confirmed') return 'preparing'
    if (status === 'preparing') return 'completed'
    return null
  }

  const currentStepIndex = useMemo(() => {
    if (!order?.status) return -1
    return STATUS_FLOW.indexOf(order.status)
  }, [order?.status])

  useEffect(() => {
    if (authLoading) return

    if (!isAuthenticated) {
      navigate('/connexion', {
        replace: true,
        state: { from: { pathname: `/commande/${id}` } },
      })
      return
    }

    const loadOrder = async () => {
      try {
        setLoading(true)

        const { data, error } = await supabase
          .from('orders')
          .select(`
          *,
          order_items (*)
        `)
          .eq('id', id)
          .single()

        if (error) {
          toast.error('Impossible de charger la commande')
          navigate('/mon-compte', { replace: true })
          return
        }

        const isOwner = data.user_id === user?.id

        if (!isOwner && !isAdmin) {
          toast.error('Accès refusé')
          navigate('/mon-compte', { replace: true })
          return
        }

        setOrder(data)
      } catch (error) {
        console.error('Erreur chargement commande :', error)
        toast.error('Une erreur est survenue lors du chargement')
        navigate('/mon-compte', { replace: true })
      } finally {
        setLoading(false)
      }
    }

    loadOrder()
  }, [authLoading, id, user, isAdmin, isAuthenticated, navigate])

  useEffect(() => {
    if (!order) return;

    trackEvent('view_order_details', {
      order_id: String(order.id),
      order_status: order.status || 'unknown',
      value: Number(order.total_amount || 0),
      currency: 'CHF',
    });
  }, [order]);

  const updateStatus = async (status) => {
    if (!isAdmin) {
      toast.error('Seul un administrateur peut modifier le statut')
      return
    }

    if (!order?.id) return

    try {
      setIsUpdatingStatus(true)

      const { data, error } = await supabase.functions.invoke('send-order-status-email', {
        body: {
          order_id: order.id,
          new_status: status,
        },
      })

      console.log('Function response:', data)
      console.log('Function error:', error)

      if (error) {
        toast.error("Erreur lors de l'appel de la fonction")
        return
      }

      if (!data?.success) {
        toast.error(data?.message || 'Erreur lors de la mise à jour')
        return
      }

      const { data: refreshedOrder, error: refreshError } = await supabase
        .from('orders')
        .select(`
        *,
        order_items (*)
      `)
        .eq('id', order.id)
        .single()

      if (refreshError) {
        toast.error('Commande mise à jour, mais rechargement impossible')
        return
      }

      setOrder(refreshedOrder)

      if (data?.skipped) {
        toast.message(data?.message || 'Aucun changement effectué')
        return
      }

      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]}`)
    } catch (error) {
      console.error('Erreur updateStatus :', error)
      toast.error('Une erreur est survenue lors de la mise à jour')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
    }).format(Number(price || 0))
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'

    return new Date(dateStr).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <main className="min-h-screen pt-20 bg-warm-alabaster">
        <section className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <div className="bg-white border border-border-subtle p-8 text-center">
            <p className="text-soft-charcoal-secondary">Chargement de la commande...</p>
          </div>
        </section>
      </main>
    )
  }

  if (!order) return null

  const nextStatus = getNextStatus(order.status)

  return (
    <main className="min-h-screen pt-20 bg-warm-alabaster">
      <section className="bg-warm-secondary py-10 lg:py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/mon-compte">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à mon compte
            </Link>
          </Button>

          <h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
            Commande {order.id}
          </h1>

          <p className="text-soft-charcoal-secondary mt-2">
            Passée le {formatDate(order.created_at)}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        <div className="bg-white border border-border-subtle p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-serif text-xl font-semibold text-espresso">
                Suivi de la commande
              </h2>
              <p className="text-soft-charcoal-secondary text-sm mt-1">
                Consultez l’avancement de votre commande en temps réel.
              </p>
            </div>

            <span
              className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'
                }`}
            >
              {STATUS_LABELS[order.status] || order.status || 'Commande confirmée'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STATUS_FLOW.map((step, index) => {
              const isDone = currentStepIndex >= index
              const isCurrent = order.status === step

              return (
                <div
                  key={step}
                  className={`border rounded-lg p-4 transition-colors ${isDone
                    ? 'border-latte-gold bg-latte-gold/10'
                    : 'border-border-subtle bg-white'
                    }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {step === 'confirmed' && <CheckCircle2 className="w-4 h-4 text-latte-gold" />}
                    {step === 'preparing' && <Clock3 className="w-4 h-4 text-latte-gold" />}
                    {step === 'completed' && <Package className="w-4 h-4 text-latte-gold" />}

                    <span className="font-medium text-espresso">
                      {STATUS_LABELS[step]}
                    </span>
                  </div>

                  <p className="text-sm text-soft-charcoal-secondary">
                    {isCurrent ? 'Étape actuelle' : isDone ? 'Terminée' : 'À venir'}
                  </p>
                </div>
              )
            })}
          </div>

          {isAdmin && (
            <div className="mt-6 pt-6 border-t border-border-subtle">
              <h3 className="font-serif text-lg font-semibold text-espresso mb-4">
                Gestion administrateur
              </h3>

              <div className="flex flex-wrap gap-3">

                {order.status === 'completed' ? (
                  <Button
                    type="button"
                    disabled
                    className="btn-secondary opacity-50 cursor-not-allowed"
                  >
                    La commande n’est plus modifiable 🔒
                  </Button>
                ) : (
                  <>
                    {order.status === 'confirmed' && (
                      <Button
                        type="button"
                        onClick={() => updateStatus('preparing')}
                        disabled={isUpdatingStatus}
                        className="btn-primary"
                      >
                        {isUpdatingStatus
                          ? 'Mise à jour...'
                          : 'Passer à : Préparation de la commande'}
                      </Button>
                    )}

                    {order.status === 'preparing' && (
                      <Button
                        type="button"
                        onClick={() => updateStatus('completed')}
                        disabled={isUpdatingStatus}
                        className="btn-primary"
                      >
                        {isUpdatingStatus
                          ? 'Mise à jour...'
                          : 'Passer à : Commande terminée'}
                      </Button>
                    )}

                    {order.status !== 'confirmed' && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => updateStatus('confirmed')}
                        disabled={isUpdatingStatus}
                        className="btn-secondary"
                      >
                        Revenir à : Commande confirmée
                      </Button>
                    )}
                  </>
                )}

              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-border-subtle p-6 lg:p-8">
          <h2 className="font-serif text-xl font-semibold text-espresso mb-6">
            Informations de la commande
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <p className="text-soft-charcoal-secondary text-sm">Prénom</p>
              <p className="font-medium text-espresso">
                {order.customer_first_name || '-'}
              </p>
            </div>

            <div>
              <p className="text-soft-charcoal-secondary text-sm">Nom</p>
              <p className="font-medium text-espresso">
                {order.customer_last_name || '-'}
              </p>
            </div>

            <div>
              <p className="text-soft-charcoal-secondary text-sm">Email</p>
              <p className="font-medium text-espresso">
                {order.customer_email || '-'}
              </p>
            </div>

            <div>
              <p className="text-soft-charcoal-secondary text-sm">Téléphone</p>
              <p className="font-medium text-espresso">
                {order.customer_phone || '-'}
              </p>
            </div>

            <div>
              <p className="text-soft-charcoal-secondary text-sm">Type de retrait</p>
              <p className="font-medium text-espresso">
                {order.pickup_type || '-'}
              </p>
            </div>

            <div>
              <p className="text-soft-charcoal-secondary text-sm">Paiement</p>
              <p className="font-medium text-espresso">
                {order.payment_status || '-'}
              </p>
            </div>
          </div>

          {order.notes && (
            <div className="mb-8">
              <p className="text-soft-charcoal-secondary text-sm mb-1">Notes client</p>
              <p className="text-espresso">{order.notes}</p>
            </div>
          )}

          {isAdmin && order.admin_notes && (
            <div className="mb-8">
              <p className="text-soft-charcoal-secondary text-sm mb-1">Notes admin</p>
              <p className="text-espresso">{order.admin_notes}</p>
            </div>
          )}

          <div className="border-t border-border-subtle pt-6">
            <h3 className="font-serif text-lg font-semibold text-espresso mb-4 flex items-center">
              <ShoppingBag className="w-5 h-5 mr-2" />
              Articles commandés
            </h3>

            {order.order_items?.length ? (
              <div className="space-y-3">
                {order.order_items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between border-b border-border-subtle pb-3"
                  >
                    <div>
                      <p className="font-medium text-espresso">
                        {item.product_name} × {item.quantity}
                      </p>
                    </div>

                    <p className="font-medium text-espresso">
                      {formatPrice(item.total_price)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-soft-charcoal-secondary">
                Aucun article trouvé pour cette commande.
              </p>
            )}

            <div className="flex items-center justify-between mt-6 pt-6 border-t border-border-subtle">
              <span className="font-serif text-lg font-semibold text-espresso">
                Total
              </span>

              <span className="font-serif text-xl font-semibold text-espresso">
                {formatPrice(order.total_amount)}
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}